<template>
  <slot name="action">
    <button
      type="button"
      class="button"
      v-on:click="openModal = true"
      v-bind="$attrs"
      :title="title"
      :disabled="!canEdit"
    >
      <i class="icon-edit"></i>
    </button>
  </slot>

  <Modal
    v-if="canEdit"
    v-model="openModal"
    :title="title"
    is-large
  >
    <form
      class="form"
      :id="`edit-${worker.id}`"
      v-on:submit.prevent="update"
    >
      <div class="field">
        <label class="label">Name</label>
        <div class="control">
          <input
            autofocus
            class="input"
            :class="{ 'is-danger': fieldErrors.name }"
            type="text"
            maxlength="100"
            v-model.trim="payload.name"
            :disabled="loading || undefined"
          />
          <template v-if="fieldErrors.name">
            <p
              class="help is-danger"
              v-for="err in fieldErrors.name"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>

      <div class="field">
        <label class="label">Slug</label>
        <div class="control">
          <input
            class="input"
            :class="{ 'is-danger': fieldErrors.slug }"
            type="text"
            maxlength="100"
            v-model.trim="payload.slug"
            :disabled="loading || undefined"
          />
          <template v-if="fieldErrors.slug">
            <p
              class="help is-danger"
              v-for="err in fieldErrors.slug"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>

      <div class="field">
        <label class="label">Type</label>
        <div class="control">
          <span class="select">
            <select
              v-model="payload.type"
              :class="{ 'is-danger': fieldErrors.type }"
              :disabled="loading || undefined"
            >
              <option
                v-for="(type, id) in workerTypes"
                :key="id"
                :value="type.slug"
              >
                {{ truncateShort(type.display_name) }}
              </option>
            </select>
          </span>
          <template v-if="fieldErrors.type">
            <p
              class="help is-danger"
              v-for="err in fieldErrors.type"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>

      <div class="field">
        <label class="label">Description</label>
        <div class="control">
          <textarea
            class="textarea"
            :class="{ 'is-danger': fieldErrors.description }"
            type="text"
            v-model.trim="payload.description"
            :disabled="loading || undefined"
          ></textarea>
          <template v-if="fieldErrors.description">
            <p
              class="help is-danger"
              v-for="err in fieldErrors.description"
              :key="err"
            >
              {{ err }}
            </p>
          </template>
        </div>
      </div>
    </form>

    <template v-slot:footer="{ close }">
      <button
        type="button"
        class="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        type="submit"
        :form="`edit-${worker.id}`"
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        :disabled="loading || undefined"
      >
        Update
      </button>
    </template>
  </Modal>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { isEmpty } from 'lodash'
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { CreateWorkerPayload } from '@/api'
import Modal from '@/components/Modal.vue'
import { ensureArray, errorParser } from '@/helpers'
import { truncateMixin } from '@/mixins'
import { useAuthStore, useNotificationStore, useWorkerStore, useWorkerTypeStore } from '@/stores'
import type { Worker } from '@/types/worker'

export default defineComponent({
  components: {
    Modal,
  },
  emits: {
    'update:worker'(value: Worker) {
      return value.id !== undefined
    },
  },
  props: {
    worker: {
      type: Object as PropType<Worker>,
      required: true,
    },
  },
  mixins: [truncateMixin],
  data: () => ({
    openModal: false,
    payload: {
      name: '',
      slug: '',
      description: '',
      type: '',
    } as CreateWorkerPayload,
    loading: false,
    fieldErrors: {} as Partial<Record<keyof CreateWorkerPayload, string[]>>,
  }),
  computed: {
    ...mapState(useAuthStore, ['user']),
    ...mapState(useWorkerTypeStore, ['workerTypes']),
    canEdit(): boolean {
      return (
        this.user !== false &&
        this.user !== null &&
        (this.user.is_admin || this.user.can_manage_workers)
      )
    },
    title(): string {
      if (!this.canEdit) return 'You do not have the necessary permission to edit workers'
      return `Edit worker ${this.truncateShort(this.worker.name)}`
    },
  },
  methods: {
    ...mapActions(useWorkerStore, ['updateWorker']),
    ...mapActions(useWorkerTypeStore, { listWorkerTypes: 'list' }),
    ...mapActions(useNotificationStore, ['notify']),
    async update() {
      if (this.loading) return
      this.loading = true
      this.fieldErrors = {}
      try {
        const value = await this.updateWorker(this.worker.id, this.payload)
        this.notify({ type: 'info', text: 'Worker updated successfully.' })
        this.openModal = false
        this.$emit('update:worker', value)
      } catch (err) {
        this.notify({ type: 'error', text: errorParser(err) })
        if (isAxiosError(err)) {
          this.fieldErrors = err?.response?.data
            ? Object.fromEntries(
                Object.entries(err?.response?.data).map(([k, v]) => [k, ensureArray(v)]),
              )
            : {}
        }
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    worker: {
      immediate: true,
      async handler() {
        this.payload.name = this.worker.name
        this.payload.slug = this.worker.slug
        this.payload.description = this.worker.description
        this.payload.type = this.worker.type
        if (isEmpty(this.workerTypes)) {
          try {
            await this.listWorkerTypes()
          } catch (err) {
            this.notify({
              type: 'error',
              text: `An error occurred listing worker types: ${errorParser(err)}`,
            })
          }
        }
      },
    },
  },
})
</script>
